import { useState } from "react";
import type { ProfitMonth } from "@/api/reports";
import { formatInr } from "@/lib/format";
import { cn } from "@/lib/utils";

/* Money in and out by month, as paired columns: plain HTML and CSS, so it reflows with the
   card instead of scaling its text like an SVG viewBox would. Blue is money in, gold is money
   out; the legend names both, the tooltip gives the figures and the table under it has them
   all, so nothing rests on colour. */

const TICKS = 4;
const compact = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

/** A round step for `TICKS` gridlines that reach `max`: 1, 2, 2.5 or 5 times a power of ten. */
export function axisStep(max: number): number {
  if (!(max > 0)) return 1;
  const raw = max / TICKS;
  const power = 10 ** Math.floor(Math.log10(raw));
  const multiple = [1, 2, 2.5, 5, 10].find((m) => m * power >= raw) ?? 10;
  return multiple * power;
}

function amount(value: string): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function Swatch({ className }: { className: string }) {
  return <span className={cn("inline-block h-2.5 w-2.5 shrink-0 rounded-sm", className)} aria-hidden />;
}

export function MoneyFlowLegend({ months }: { months: ProfitMonth[] }) {
  const total = (pick: (m: ProfitMonth) => string) =>
    months.reduce((sum, m) => sum + Math.round(amount(pick(m)) * 100), 0) / 100;
  const moneyIn = total((m) => m.income_inr);
  const moneyOut = total((m) => m.expenses_inr);
  const net = moneyIn - moneyOut;
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
      <div className="flex items-center gap-2">
        <Swatch className="bg-money-in" />
        <dt className="text-muted-foreground">In</dt>
        <dd className="font-semibold tabular-nums">{formatInr(moneyIn.toFixed(2))}</dd>
      </div>
      <div className="flex items-center gap-2">
        <Swatch className="bg-money-out" />
        <dt className="text-muted-foreground">Out</dt>
        <dd className="font-semibold tabular-nums">{formatInr(moneyOut.toFixed(2))}</dd>
      </div>
      <div className="flex items-center gap-2">
        <dt className="text-muted-foreground">Net</dt>
        <dd className={cn("font-semibold tabular-nums", net < 0 && "text-destructive")}>
          {formatInr(net.toFixed(2))}
        </dd>
      </div>
    </dl>
  );
}

export function MoneyFlowChart({ months }: { months: ProfitMonth[] }) {
  const [active, setActive] = useState<number | null>(null);
  const values = months.map((m) => ({ in: amount(m.income_inr), out: amount(m.expenses_inr) }));
  const max = Math.max(0, ...values.map((v) => Math.max(v.in, v.out)));
  const step = axisStep(max);
  const top = step * TICKS;
  const height = (value: number) =>
    value > 0 ? `max(2px, ${Math.min(100, (value / top) * 100)}%)` : "0";
  const last = months.length - 1;
  const shown = active === null ? null : months[active];

  return (
    <figure className="flex flex-col gap-2">
      {/* The plot is for the eye; the table under it carries the same figures. */}
      <div className="relative h-56 pl-11 sm:h-64" aria-hidden>
        {Array.from({ length: TICKS + 1 }, (_, i) => (
          <div
            key={i}
            className={cn("absolute left-11 right-0 border-t", i === 0 ? "border-input" : "border-border/70")}
            style={{ bottom: `${(i / TICKS) * 100}%` }}
          >
            <span className="absolute -left-11 top-0 w-9 -translate-y-1/2 text-right text-[11px] tabular-nums leading-none text-muted-foreground">
              {compact.format(i * step)}
            </span>
          </div>
        ))}
        <div className="absolute inset-0 left-11 flex" onMouseLeave={() => setActive(null)}>
          {months.map((month, i) => (
            <div
              key={month.month}
              onMouseEnter={() => setActive(i)}
              className={cn(
                "flex h-full min-w-0 flex-1 items-end justify-center gap-[2px] rounded-sm px-[3px] transition-colors",
                active === i && "bg-accent/70",
              )}
            >
              <span
                className="w-full max-w-[16px] rounded-t-[3px] bg-money-in"
                style={{ height: height(values[i]?.in ?? 0) }}
              />
              <span
                className="w-full max-w-[16px] rounded-t-[3px] bg-money-out"
                style={{ height: height(values[i]?.out ?? 0) }}
              />
            </div>
          ))}
        </div>
        {shown && active !== null && (
          <div
            className={cn(
              "pointer-events-none absolute top-1 z-10 w-48 rounded-md border bg-card px-3 py-2 text-xs shadow-md",
              active < 3 ? "" : active > last - 3 ? "-translate-x-full" : "-translate-x-1/2",
            )}
            style={{
              left: `calc(2.75rem + (100% - 2.75rem) * ${(active < 3 ? active : active > last - 3 ? active + 1 : active + 0.5) / months.length})`,
            }}
          >
            <p className="mb-1 font-semibold text-foreground">{shown.label}</p>
            <p className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Swatch className="bg-money-in" /> In
              </span>
              <span className="tabular-nums">{formatInr(shown.income_inr)}</span>
            </p>
            <p className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <Swatch className="bg-money-out" /> Out
              </span>
              <span className="tabular-nums">{formatInr(shown.expenses_inr)}</span>
            </p>
            <p className="mt-1 flex justify-between gap-2 border-t pt-1 font-semibold">
              <span>Net</span>
              <span className={cn("tabular-nums", amount(shown.profit_inr) < 0 && "text-destructive")}>
                {formatInr(shown.profit_inr)}
              </span>
            </p>
          </div>
        )}
      </div>
      <div className="flex pl-11" aria-hidden>
        {months.map((month, i) => (
          <span
            key={month.month}
            className={cn(
              "min-w-0 flex-1 text-center text-[11px] text-muted-foreground",
              // Every other month on a phone, always keeping the latest.
              (last - i) % 2 === 1 && "invisible sm:visible",
              i === last && "font-semibold text-foreground",
            )}
          >
            {month.label.slice(0, 3)}
          </span>
        ))}
      </div>
      <figcaption className="sr-only">
        Money in and out by month, in rupees; the figures are in the table that follows.
      </figcaption>
    </figure>
  );
}

/** The chart's figures as a table, for exact numbers and for screen readers. */
export function MoneyFlowTable({ months }: { months: ProfitMonth[] }) {
  return (
    <div className="table-frame shadow-none">
      <table className="data-table data-table-compact">
        <thead>
          <tr>
            <th>Month</th>
            <th className="text-right">In</th>
            <th className="text-right">Out</th>
            <th className="text-right">Net</th>
          </tr>
        </thead>
        <tbody>
          {[...months].reverse().map((month) => (
            <tr key={month.month}>
              <td className="whitespace-nowrap">{month.label}</td>
              <td className="whitespace-nowrap text-right">{formatInr(month.income_inr)}</td>
              <td className="whitespace-nowrap text-right">{formatInr(month.expenses_inr)}</td>
              <td
                className={cn(
                  "whitespace-nowrap text-right font-medium",
                  amount(month.profit_inr) < 0 && "text-destructive",
                )}
              >
                {formatInr(month.profit_inr)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
